import React, { useEffect, useRef } from "react";
import { Provider, useDispatch, useSelector } from 'react-redux';
import { HashRouter } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import store, { history } from './store';
import "bootstrap/dist/css/bootstrap.min.css";
import "../custom.css";
import MainContent from "./wajujogido/MainContent/MainContent";
import ToggleMenu from "../components/ToggleMenu/ToggleMenu";
import { HubConnectionBuilder } from '@microsoft/signalr';
import { actions as usersAction } from '../reducers/users';
import {
  attemptKeepalivePendingExamUploadsCleanup,
  cleanupPendingExamUploads,
  hasPendingExamUploads,
  markAllPendingExamUploadsForCleanup,
} from '../utils/examUploadCleanup';
import { isExplicitLogoutFlowActive } from '../utils/logoutFlow';

const SignalRListener = () => {
  const dispatch = useDispatch();
  const userToken = useSelector(state => state.users.userToken);
  const connectionRef = useRef(null);

  useEffect(() => {
    if (!userToken) {
      // Si no hay token, desconecta si estaba conectado
      if (connectionRef.current) {
        connectionRef.current.stop();
        connectionRef.current = null;
      }
      return;
    }

    // Si hay token, crea la conexión
    const connection = new HubConnectionBuilder()
      .withUrl("/sessionHub", {
        accessTokenFactory: () => userToken
      })
      .withAutomaticReconnect()
      .build();

    const handleSessionInvalidated = () => {
      alert("Se ha iniciado sesión en otro dispositivo. Tu sesión ha sido cerrada.");
      dispatch(usersAction.resetUser());
    };

    connection.start().then(() => {
      // Limpia cualquier handler anterior antes de añadir uno nuevo
      connection.off("SessionInvalidated");
      connection.on("SessionInvalidated", handleSessionInvalidated);
    });

    connectionRef.current = connection;

    return () => {
      if (connectionRef.current) {
        connectionRef.current.off("SessionInvalidated", handleSessionInvalidated);
        connectionRef.current.stop();
        connectionRef.current = null;
      }
    };
  }, [userToken, dispatch]);

  return null;
};

const PendingExamUploadCleanupListener = () => {
  const authenticated = useSelector(state => state.users.authenticated);
  const userToken = useSelector(state => state.users.userToken);
  const cleanupInFlight = useRef(false);
  const authenticatedUserId = authenticated?.id;

  useEffect(() => {
    if (!authenticatedUserId || !userToken) {
      return undefined;
    }

    let isMounted = true;

    const runCleanup = async () => {
      if (cleanupInFlight.current || !hasPendingExamUploads(authenticatedUserId, { cleanupRequiredOnly: true })) {
        return;
      }

      cleanupInFlight.current = true;

      try {
        await cleanupPendingExamUploads({
          userId: authenticatedUserId,
          token: userToken,
        });
      } finally {
        if (isMounted) {
          cleanupInFlight.current = false;
        }
      }
    };

    const handleOnline = () => {
      runCleanup();
    };

    const handlePageHide = () => {
      if (isExplicitLogoutFlowActive()) {
        return;
      }

      if (!hasPendingExamUploads(authenticatedUserId)) {
        return;
      }

      markAllPendingExamUploadsForCleanup(authenticatedUserId);
      attemptKeepalivePendingExamUploadsCleanup({
        userId: authenticatedUserId,
        token: userToken,
      });
    };

    if (window.navigator.onLine) {
      runCleanup();
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      isMounted = false;
      cleanupInFlight.current = false;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [authenticatedUserId, userToken]);

  return null;
};

const App = () => {
  // Escucha cambios en el almacenamiento local para detectar cierres de sesión en otras pestañas
	useEffect(() => {
		const onStorage = (event) => {
			if (event.key === "logout") {
				alert("Se ha cerrado la sesión. Serás redirigido a la página de inicio.");
				window.location.href = "/";
			}
		};
		window.addEventListener("storage", onStorage);
		return () => window.removeEventListener("storage", onStorage);
	}, []);

	return (
		<div className='app-root'>
			<Provider store={store}>
				<SignalRListener />
        <PendingExamUploadCleanupListener />
				<ConnectedRouter history={history}>
					<HashRouter>
						<ToggleMenu />
					</HashRouter>
					<MainContent history={history} />
				</ConnectedRouter>
			</Provider>
		</div>
	);
};

export default App;
